import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Sparkles, X, FileText, Shield } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { getReferralSource, clearReferralSource } from '../hooks/useReferralTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Auth = ({ setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = (() => {
    const sp = new URLSearchParams(location.search);
    const n = sp.get('next');
    // Only allow same-origin internal paths to prevent open-redirect
    return n && n.startsWith('/') && !n.startsWith('//') ? n : null;
  })();
  const { t } = useLanguage();
  const [mode, setMode] = useState('signin'); // 'signin' or 'signup'
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    role: '',
    agreed_to_terms: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [terms, setTerms] = useState(null);

  // Fetch terms when entering signup
  useEffect(() => {
    if (mode === 'signup' && !terms) {
      axios.get(`${API}/terms/cleaner`).then(res => setTerms(res.data)).catch(() => {});
    }
  }, [mode, terms]);

  const reset = (newMode) => {
    setMode(newMode);
    setStep(1);
    setError('');
  };

  const goBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setError('');
    } else {
      navigate('/');
    }
  };

  const submitLogin = async () => {
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      localStorage.setItem('token', res.data.token);
      setUser(res.data.user);
      const role = res.data.user.role;
      navigate(nextPath || (role === 'admin' ? '/dashboard/admin' : role === 'cleaner' ? '/dashboard/cleaner' : '/dashboard/customer'));
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.signinFailed', 'Sign in failed'));
    } finally { setLoading(false); }
  };

  const submitSignup = async () => {
    setError(''); setLoading(true);
    try {
      const signupSource = getReferralSource();
      const res = await axios.post(`${API}/auth/register`, {
        ...formData,
        ...(signupSource ? { signup_source: signupSource } : {}),
      });
      localStorage.setItem('token', res.data.token);
      if (signupSource) clearReferralSource();
      setUser(res.data.user);
      navigate(nextPath || (formData.role === 'cleaner' ? '/dashboard/cleaner' : '/dashboard/customer'));
    } catch (err) {
      setError(err.response?.data?.detail || 'Sign up failed');
    } finally { setLoading(false); }
  };

  const handleContinue = (e) => {
    e?.preventDefault();
    setError('');

    if (mode === 'signin') {
      if (step === 1) {
        if (!formData.email.includes('@')) { setError('Please enter a valid email'); return; }
        setStep(2);
      } else {
        if (!formData.password) { setError('Please enter your password'); return; }
        submitLogin();
      }
    } else {
      // signup
      if (step === 1) {
        if (!formData.email.includes('@')) { setError('Please enter a valid email'); return; }
        setStep(2);
      } else if (step === 2) {
        if (!formData.name.trim() || !formData.phone.trim()) { setError('Please fill all fields'); return; }
        setStep(3);
      } else if (step === 3) {
        if (formData.password.length < 6) { setError('Password must be at least 6 characters'); return; }
        setStep(4);
      } else if (step === 4) {
        if (!formData.role) { setError('Please select a role'); return; }
        // Customers skip terms step, cleaners go to step 5
        if (formData.role === 'cleaner') {
          setStep(5);
        } else {
          submitSignup();
        }
      } else {
        // step 5: cleaner terms
        if (!formData.agreed_to_terms) { setError(t('auth.mustAgree', 'You must agree to the Cleaner Platform Agreement to continue')); return; }
        submitSignup();
      }
    }
  };

  // Step content
  const stepConfig = mode === 'signin' ? {
    1: { title: t('auth.step1Title', "What's your email?"), subtitle: t('auth.step1SubtitleSignin', 'Enter the email you signed up with.'), input: 'email' },
    2: { title: t('auth.step2TitleSignin', 'Welcome back'), subtitle: t('auth.step2SubtitleSignin', 'Enter your password to continue.'), input: 'password' }
  } : {
    1: { title: t('auth.step1Title', "What's your email?"), subtitle: t('auth.step1SubtitleSignup', "We'll use this to keep you posted about your bookings."), input: 'email' },
    2: { title: t('auth.step2TitleSignup', 'Tell us about you'), subtitle: t('auth.step2SubtitleSignup', 'Your name and a phone number we can reach you at.'), input: 'profile' },
    3: { title: t('auth.step3Title', 'Create a password'), subtitle: t('auth.step3Subtitle', 'At least 6 characters. Make it memorable.'), input: 'password' },
    4: { title: t('auth.step4Title', 'I am a...'), subtitle: t('auth.step4Subtitle', "Choose how you'll use Match My Cleaner."), input: 'role' },
    5: { title: t('auth.step5Title', 'Cleaner Platform Agreement'), subtitle: t('auth.step5Subtitle', 'Please review and accept to complete your sign-up.'), input: 'terms' }
  };

  const current = stepConfig[step];
  const totalSteps = mode === 'signin' ? 2 : (formData.role === 'cleaner' ? 5 : 4);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 md:px-12 lg:px-16 py-5 border-b border-border">
        <button onClick={goBack} className="p-2 -ml-2 hover:bg-surface-muted rounded-full transition-all" data-testid="auth-back-button">
          {step > 1 ? <ArrowLeft className="w-5 h-5 text-text-primary" /> : <X className="w-5 h-5 text-text-primary" />}
        </button>
        <h1 className="font-heading text-xl font-bold text-primary cursor-pointer" onClick={() => navigate('/')}>Match My Cleaner</h1>
        <div className="w-9" />
      </div>

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2 py-6">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i + 1 === step ? 'w-8 bg-primary' : i + 1 < step ? 'w-4 bg-primary' : 'w-4 bg-border'
            }`}
          />
        ))}
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center px-6 md:px-12 lg:px-16 pb-10">
        <div className="w-full max-w-md">
          <form onSubmit={handleContinue} data-testid="uber-auth-form">
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-text-primary mb-3 leading-tight" data-testid="auth-step-title">
              {current.title}
            </h2>
            <p className="text-text-secondary text-lg mb-10" data-testid="auth-step-subtitle">{current.subtitle}</p>

            {/* INPUTS PER STEP */}
            {current.input === 'email' && (
              <div className="mb-8">
                <input
                  autoFocus
                  type="email"
                  inputMode="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-field input-large w-full"
                  placeholder={t('auth.emailPlaceholder', 'you@example.com')}
                  data-testid="step-email-input"
                />
              </div>
            )}

            {current.input === 'password' && (
              <div className="mb-8">
                <input
                  autoFocus
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-field input-large w-full"
                  placeholder="••••••••"
                  data-testid="step-password-input"
                />
                {mode === 'signin' && (
                  <p className="text-sm text-text-secondary mt-3">
                    {t('auth.signingInAs', 'Signing in as')} <span className="font-semibold text-text-primary">{formData.email}</span>
                  </p>
                )}
              </div>
            )}

            {current.input === 'profile' && (
              <div className="space-y-4 mb-8">
                <input
                  autoFocus
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field input-large w-full"
                  placeholder={t('auth.namePlaceholder', 'Full name')}
                  data-testid="step-name-input"
                />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-field input-large w-full"
                  placeholder={t('auth.phonePlaceholder', 'Phone number')}
                  data-testid="step-phone-input"
                />
              </div>
            )}

            {current.input === 'role' && (
              <div className="space-y-3 mb-8">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'customer' })}
                  className={`w-full p-5 rounded-2xl border-2 text-left transition-all duration-300 ${
                    formData.role === 'customer' ? 'border-primary bg-primary-light' : 'border-border hover:border-secondary'
                  }`}
                  data-testid="role-customer-card"
                >
                  <h3 className="font-heading font-bold text-lg text-text-primary mb-1">{t('auth.imCustomer', "I'm a Customer")}</h3>
                  <p className="text-sm text-text-secondary">{t('auth.imCustomerBody', 'I want to book cleaning services for my home or office.')}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'cleaner' })}
                  className={`w-full p-5 rounded-2xl border-2 text-left transition-all duration-300 ${
                    formData.role === 'cleaner' ? 'border-primary bg-primary-light' : 'border-border hover:border-secondary'
                  }`}
                  data-testid="role-cleaner-card"
                >
                  <h3 className="font-heading font-bold text-lg text-text-primary mb-1">{t('auth.imCleaner', "I'm a Cleaner")}</h3>
                  <p className="text-sm text-text-secondary">{t('auth.imCleanerBody', 'I want to offer my cleaning services and earn money.')}</p>
                </button>
              </div>
            )}

            {current.input === 'terms' && (
              <div className="mb-8" data-testid="terms-step">
                <div className="bg-surface-muted border border-border rounded-2xl p-6 mb-5 max-h-[320px] overflow-y-auto">
                  <div className="flex items-center gap-2 mb-3 sticky top-0 bg-surface-muted">
                    <Shield className="w-5 h-5 text-primary" />
                    <h3 className="font-heading font-bold text-text-primary">{t('auth.platformAgreement', 'Platform Agreement')}</h3>
                  </div>
                  <pre className="text-xs text-text-primary whitespace-pre-wrap font-sans leading-relaxed" data-testid="terms-text">
                    {terms?.text || t('auth.loadingTerms', 'Loading terms...')}
                  </pre>
                </div>

                <label className="flex items-start gap-3 cursor-pointer p-4 border-2 border-border rounded-2xl hover:border-primary transition-all">
                  <input
                    type="checkbox"
                    checked={formData.agreed_to_terms}
                    onChange={(e) => setFormData({ ...formData, agreed_to_terms: e.target.checked })}
                    className="mt-1 w-5 h-5 accent-primary cursor-pointer"
                    data-testid="agree-terms-checkbox"
                  />
                  <div>
                    <p className="font-semibold text-text-primary mb-1">{t('auth.agreeToTerms', 'I agree to the Cleaner Platform Agreement')}</p>
                    <p className="text-sm text-text-secondary">
                      {t('auth.agreeToTermsBody', 'I commit to using only Match My Cleaner to transact with platform-found customers and acknowledge the non-circumvention terms above.')}
                    </p>
                  </div>
                </label>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-error text-error px-4 py-3 rounded-xl text-sm mb-6" data-testid="auth-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-text-primary text-white font-semibold py-4 rounded-full flex items-center justify-center gap-2 hover:bg-primary transition-all duration-300 disabled:opacity-50"
              data-testid="auth-continue-button"
            >
              {loading ? t('auth.pleaseWait', 'Please wait...') : step === totalSteps ? (mode === 'signin' ? t('auth.signinCTA', 'Sign In') : t('auth.createAccount', 'Create Account')) : t('auth.continue', 'Continue')}
              {!loading && <ArrowRight className="w-5 h-5" />}
            </button>
          </form>

          {/* Switch mode */}
          {step === 1 && (
            <div className="mt-10 text-center">
              <div className="flex items-center gap-4 mb-6">
                <div className="flex-1 h-px bg-border" />
                <span className="text-sm text-text-secondary">{t('auth.or', 'or')}</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <button
                type="button"
                onClick={() => reset(mode === 'signin' ? 'signup' : 'signin')}
                className="w-full border-2 border-border text-text-primary font-semibold py-4 rounded-full hover:border-primary hover:text-primary transition-all duration-300 flex items-center justify-center gap-2"
                data-testid="auth-switch-mode"
              >
                {mode === 'signin' ? (
                  <>
                    <Sparkles className="w-4 h-4" />
                    {t('auth.createNew', 'Create new account')}
                  </>
                ) : t('auth.alreadyHave', 'Already have an account? Sign in')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;
